import { useEffect, useState } from "react";
import type { MapCategory } from "@/map/config/categories";
import { useMapPinStore } from "@/map/stores/useMapPinStore";

interface UseMapPanelsArgs {
  /** Alert-review mode — keeps the docked video permanently mounted. */
  alertPreview: boolean;
  /** No machine means no stream subject, so no docked video. */
  hasMachine: boolean;
}

/**
 * Which surfaces are open around the map, and where the video sits.
 *
 * Owns panel STATE and the placement derived from it. Handlers that also touch
 * alert/event/seek state — `toggleCategory`, `toggleTimeline`, `handleEscape` —
 * deliberately stay in the page: they coordinate across layers, and routing them
 * through here would just move the coupling behind a callback.
 */
export const useMapPanels = ({ alertPreview, hasMachine }: UseMapPanelsArgs) => {
  const [openCategory, setOpenCategory] = useState<MapCategory | null>(null);
  const [categoryFloating, setCategoryFloating] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineFloating, setTimelineFloating] = useState(false);
  const [videoFloating, setVideoFloating] = useState(false);
  const [modeBarCollapsed, setModeBarCollapsed] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Alert-review keeps the docked video PERMANENTLY mounted: it is the review
  // surface itself, so nothing that merely reuses the category slot (opening
  // another panel, the video toggle, Esc, selecting an event / an alert that
  // isn't in the feed) may unmount it — a remount would reset playback and the
  // seek position. Popping it out still moves it (it stays visible, floating),
  // and a machine is still required: without one there is no stream subject.
  const videoAlwaysDocked = alertPreview && !videoFloating && hasMachine;
  const videoOpen = openCategory === "video" || videoFloating || videoAlwaysDocked;
  // Docked video placement. In review (Alert) mode the docked video is a fixed
  // LEFT column (only rendered when a machine is selected); otherwise it docks
  // to the right. The active side drives which neighbours shift aside.
  const videoDocked = (openCategory === "video" || videoAlwaysDocked) && !videoFloating;

  // Layout lock: when the video is pinned, freeze which side it docks to
  // (review mode → LEFT, otherwise RIGHT) at pin time so toggling the map mode
  // can't flip it. Frozen HERE rather than inside MapVideoPanel so the rail,
  // cards and timeline shifts stay in sync — they all derive from these values.
  // Playback and the streamed subject are untouched.
  const videoPinned = useMapPinStore((s) => s.isPinned("video"));
  const [frozenVideoReviewDock, setFrozenVideoReviewDock] = useState<boolean | null>(null);
  useEffect(() => {
    if (videoPinned && frozenVideoReviewDock == null && videoDocked)
      setFrozenVideoReviewDock(alertPreview);
    else if (!videoPinned && frozenVideoReviewDock != null) setFrozenVideoReviewDock(null);
  }, [videoPinned, alertPreview, videoDocked, frozenVideoReviewDock]);
  const videoReviewDock = frozenVideoReviewDock ?? alertPreview;

  const videoDockedLeft = videoDocked && videoReviewDock && hasMachine;
  const videoDockedRight = videoDocked && !videoReviewDock;

  // --- Video placement controls (pure panel moves, no cross-layer state) ---
  const popOutVideo = () => {
    setVideoFloating(true);
    setOpenCategory((prev) => (prev === "video" ? null : prev));
  };
  const dockVideo = () => {
    setVideoFloating(false);
    setOpenCategory("video");
  };
  const closeVideo = () => {
    setVideoFloating(false);
    setOpenCategory((prev) => (prev === "video" ? null : prev));
  };

  return {
    openCategory,
    setOpenCategory,
    categoryFloating,
    setCategoryFloating,
    timelineOpen,
    setTimelineOpen,
    timelineFloating,
    setTimelineFloating,
    videoFloating,
    setVideoFloating,
    modeBarCollapsed,
    setModeBarCollapsed,
    showShortcuts,
    setShowShortcuts,
    videoAlwaysDocked,
    videoOpen,
    videoDocked,
    videoReviewDock,
    videoDockedLeft,
    videoDockedRight,
    popOutVideo,
    dockVideo,
    closeVideo,
  };
};
